import "./index.css";
import { QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { GarageGate } from "./components/app/garages";
import { TooltipProvider } from "./components/ui/tooltip";
import { AccountApp } from "./features/account/account-app";
import { AdminApp } from "./features/admin/admin-app";
import { QrEntry } from "./features/labels/qr-entry";
import { i18nReady } from "./i18n";
import { setAuthMode, signsIn } from "./lib/account";
import { canonicalizeUrl, garage, section } from "./lib/garage";
import { countInteraction, routeInteraction } from "./lib/interactions";
import { AccountPreferences } from "./lib/preferences";
import { createQueryClient } from "./lib/query";
import { setInitialSystemInfo } from "./lib/system";
import { ThemeProvider } from "./lib/theme";
import { TRPCProvider, trpcClient } from "./lib/trpc";
import { routeTree } from "./routeTree.gen";

const queryClient = createQueryClient();

// The router runs below the garage's address: /my-garage/inventory is its /inventory.
const router = createRouter({
  routeTree,
  basepath: `/${garage ?? ""}`,
  context: { queryClient },
  defaultPreload: "intent",
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
});

// Opening a page counts, for someone who shares usage statistics (ADR 0016). Preloads don't
// resolve, so hovering a link counts nothing; a changed filter on the same page counts nothing.
router.subscribe("onResolved", (event) => {
  if (!event.pathChanged) return;
  const match = router.state.matches.at(-1);
  const interaction = match ? routeInteraction(match.routeId) : null;
  if (interaction) countInteraction(interaction.area, interaction.action);
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

/** /account/… and /admin/… are the app's own pages; anything else is a garage (or "/"). */
function App() {
  if (section === "account") return <AccountApp />;
  if (section === "admin") return <AdminApp />;
  if (section === "qr") return <QrEntry />;
  return garage ? <RouterProvider router={router} /> : <GarageGate />;
}

async function start() {
  if (!canonicalizeUrl()) return;
  // Whether people sign in here decides what the pages show (ADR 0011).
  const [info] = await Promise.all([trpcClient.system.info.query().catch(() => null), i18nReady]);
  setAuthMode(
    info?.auth ?? "none",
    info?.publicDemo ?? null,
    info?.localUsers ?? false,
    info?.passkeys ?? false,
  );
  setInitialSystemInfo(info);
  createRoot(document.getElementById("root") as HTMLElement).render(
    <StrictMode>
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <TRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
            <TooltipProvider delayDuration={300}>
              {signsIn() ? <AccountPreferences /> : null}
              <App />
            </TooltipProvider>
          </TRPCProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </StrictMode>,
  );
}

void start();
